/**
 * QuotePreviewScheduleModal.tsx
 * Phase 5, Item 5 — "Preview Schedule" modal for the Quote editor.
 *
 * Opens a read-only Highcharts Gantt scoped to the given quote's tentative
 * machine bookings.  The backend endpoint is called with `?quoteKey=...`
 * so only Bucket B (quote-sourced bookings) is returned.
 *
 * Props:
 *   open       — controls dialog visibility
 *   onClose    — close handler
 *   quoteKey   — the QuoteKey UUID to preview
 *   quoteNumber — display label for the dialog title
 *
 * Read-only: no onPointClick is passed to ScheduleGantt, so bars are not
 * clickable.  The navigator and scrollbar are rendered but no drag/resize.
 *
 * Date range: automatically derived from the returned bookings (earliest
 * start − 7 days, latest end + 7 days).  Falls back to today ± 90 days
 * when the quote has no bookings yet.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { CalendarViewMonth } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ScheduleGantt from '../../../components/schedule/ScheduleGantt.js';
import {
  buildGanttChartData,
  type GanttColorTokens,
  type GanttChartData,
} from '../../../components/schedule/masterGanttRowBuilder.js';
import {
  fetchMasterGanttData,
} from '../../../services/scheduleMasterGantt.js';
import type { MasterGanttResponse } from '../../../types/schedule.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface QuotePreviewScheduleModalProps {
  open: boolean;
  onClose: () => void;
  quoteKey: string;
  quoteNumber: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** 'YYYY-MM-DD' ± N days → 'YYYY-MM-DD'. */
function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Return today ± deltaDays as 'YYYY-MM-DD'. */
function isoToday(deltaDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/**
 * Derive chart date range from a MasterGanttResponse.
 * Looks at all machineBookings across all projects/workOrders.
 * Returns today −7 / today +90 when no bookings exist.
 */
function deriveRange(data: MasterGanttResponse): { startDate: string; endDate: string } {
  let earliest: string | null = null;
  let latest: string | null = null;
  for (const project of data.projects) {
    for (const wo of project.workOrders) {
      for (const b of wo.machineBookings) {
        if (!earliest || b.startDate < earliest) earliest = b.startDate;
        if (!latest   || b.endDate   > latest)   latest   = b.endDate;
      }
    }
  }
  if (!earliest || !latest) {
    return { startDate: isoToday(-7), endDate: isoToday(90) };
  }
  return {
    startDate: shiftDate(earliest, -7),
    endDate:   shiftDate(latest,    7),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const QuotePreviewScheduleModal: React.FC<QuotePreviewScheduleModalProps> = ({
  open,
  onClose,
  quoteKey,
  quoteNumber,
}) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ganttData, setGanttData] = useState<MasterGanttResponse | null>(null);

  // ── Color tokens for the row builder ──────────────────────────────────────
  const colors: GanttColorTokens = {
    committed:       theme.schedule.committed,
    tentative:       theme.schedule.tentative,
    machineTime:     theme.schedule.machineTime,
    employeeAlloc:   theme.schedule.employeeAlloc,
    maintenance:     theme.schedule.maintenance,
    outOfService:    theme.schedule.outOfService,
    projectEnvelope: theme.schedule.projectEnvelope,
    workOrderEnvelope: theme.schedule.workOrderEnvelope,
    continuous:      theme.schedule.continuous,
  };

  // ── Load quote-scoped gantt data when the modal opens ────────────────────
  const loadData = useCallback(async () => {
    if (!quoteKey) return;
    setLoading(true);
    setError(null);
    try {
      // Use a wide window so all bookings are captured regardless of their dates.
      // The date range shown to the user is then derived from actual booking dates.
      const data = await fetchMasterGanttData(
        isoToday(-365),
        isoToday(730),
        quoteKey,
      );
      setGanttData(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load schedule preview.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [quoteKey]);

  // Reset and reload whenever the modal opens.
  useEffect(() => {
    if (!open) {
      setGanttData(null);
      setError(null);
      return;
    }
    void loadData();
  }, [open, loadData]);

  // ── Derive chart range + build series ────────────────────────────────────
  const dateRange = ganttData ? deriveRange(ganttData) : null;
  const chartData: GanttChartData | null = ganttData
    ? buildGanttChartData(ganttData, colors)
    : null;

  const hasBookings =
    ganttData !== null &&
    ganttData.projects.some((p) => p.workOrders.some((w) => w.machineBookings.length > 0));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 700,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 1.5,
        }}
      >
        <CalendarViewMonth sx={{ color: theme.schedule.tentative }} />
        Schedule Preview &mdash; {quoteNumber}
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: loading ? 0 : 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: theme.palette.text.secondary,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2">Loading schedule…</Typography>
          </Box>
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {!loading && error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Empty state ──────────────────────────────────────────────────── */}
        {!loading && !error && ganttData && !hasBookings && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: theme.palette.text.disabled,
            }}
          >
            <CalendarViewMonth sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography variant="body1">No machine bookings for this quote yet.</Typography>
            <Typography variant="caption">
              Use &ldquo;Add to Schedule&rdquo; to generate tasks and place them on machines.
            </Typography>
          </Box>
        )}

        {/* ── Gantt chart (read-only — no onPointClick) ────────────────────── */}
        {!loading && !error && chartData && dateRange && hasBookings && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ScheduleGantt
              series={chartData.series}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              height={Math.max(
                300,
                (chartData.series[0]?.data.length ?? 0) * 26 + 100,
              )}
              // No onPointClick → read-only (no navigation on bar click)
              // No conflictColors → conflict glyphs are not shown in this view
              //   (the preview is informational; conflict details are on the Machine Schedule)
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1 }}
        >
          Read-only preview. Use the Machine Schedule for editing.
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotePreviewScheduleModal;
